import * as React from 'react';
import * as autoBind from 'react-autobind';
import DateDisplay from '../DateDisplay/DateDisplay';
import ReviewModel from '../../types/ReviewModel';
import ReviewService from '../../services/ReviewService/ReviewService';
import TimeFormatter from '../../utils/TimeFormatter/TimeFormatter';
import Constants from '../../globals/Constants';
import DatePicker from 'react-datepicker';
import Spinner from '../LoadingSpinner/Spinner';
import UserMessaging from '../../utils/UserMessaging';
import Strings from '../../globals/Strings';
import {ModalStates} from '../../globals/Constants';
import GoogleAnalyticsService from '../../services/GoogleAnalyticsService/GoogleAnalyticsService';
import ModalEventHandlersModel from '../../types/ModalEventHandlers';
import Themeable from '../../types/Themeable';
import UpdateReviewModel from '../../types/UpdateReviewModel';
import * as ReactDOM from 'react-dom';

export default class ReviewByDate extends React.Component<ReviewByDateProps, ReviewByDateState> {
   readonly state: ReviewByDateState = {isSaveInProgress: false};
   datepicker: any;
   inputRef: any;
   ignoreInputClick: boolean;

   constructor(props: ReviewByDateProps) {
      super(props);
      autoBind(this);

      this.inputRef = React.createRef();
   }

   handleDatePickerSelection(date: Date): void {
      if (!this.props.review.ReviewByDate && date) {
         this.handleDatePickerChange(date);
      }
   }

   updateReviewByDate(review: ReviewModel): Promise<void> {
      const userId = this.props.currentUserId;
      const updatedReview: UpdateReviewModel = {
         Id: review.Id,
         Title: null,
         ReviewByDate: review.ReviewByDate,
         PrivacyType: null,
         Description: null,
         SendNotifications: review.SendNotifications
      };

      return ReviewService.updateReview(updatedReview).then(() => {
         GoogleAnalyticsService.trackReviewByDateChanged(userId, Constants.googleAnalytics.reviewByDate.actions.changed);
         this.setState({isSaveInProgress: false});
      }).catch(() => {
         UserMessaging.warn(Strings.errors.failedSaveReviewByDate);
         this.setState({isSaveInProgress: false});
      });
   }

   handleDatePickerChange(date: Date): void {
      const {review, currentUserId, setModalState} = this.props;
      this.setState({isSaveInProgress: true});
      const updatedReview = Object.assign({}, review, {ReviewByDate: date});

      if (date === null) {
         this.updateReviewByDate(updatedReview);
         return;
      }

      const modalEventHandlers = {
         onConfirm: () => {
            updatedReview.SendNotifications = true;
            return this.updateReviewByDate(updatedReview).then(() => {
               GoogleAnalyticsService.trackReviewByDateChanged(currentUserId, Constants.googleAnalytics.reviewByDate.actions.send);
            });
         },
         onClose: () => {
            updatedReview.SendNotifications = false;
            return this.updateReviewByDate(updatedReview).then(() => {
               GoogleAnalyticsService.trackReviewByDateChanged(currentUserId, Constants.googleAnalytics.reviewByDate.actions.dontSend);
            });
         },
         onDismiss: () => {
            updatedReview.SendNotifications = false;
            return this.updateReviewByDate(updatedReview).then(() => {
               GoogleAnalyticsService.trackReviewByDateChanged(currentUserId, Constants.googleAnalytics.reviewByDate.actions.dontSend);
            });
         }};

      setModalState(ModalStates.changeReviewByDate, modalEventHandlers);
   }

   onClickOutside(e: any) {
      if (this.inputRef && this.inputRef.current.contains(e.target)) {
         this.ignoreInputClick = true;
      }
   }

   handleClickInput() {
      this.ignoreInputClick = false;
   }

   removeDate() {
      this.handleDatePickerChange(null);
   }

   render() {
      const reviewLoadingSpinner = <div className="loading-spinner deadline-loading-spinner">{Spinner}</div>;
      const {review, currentUserId, themeName} = this.props;
      const {isSaveInProgress} = this.state;
      const defaultDate = review.ReviewByDate ? review.ReviewByDate : new Date();
      const displayDate = TimeFormatter.getReviewByDateAdjustedFromLocalTimezone(review.ReviewByDate);
      const isOwner = currentUserId && review.Reviewers && review.Reviewers.find(reviewer => reviewer.Id === currentUserId && reviewer.Role === Constants.reviewerRoles.owner);

      const calendarContainer = ({children}: any) => {
         return ReactDOM.createPortal(
            <React.Fragment>
               {children}
            </React.Fragment>,
            document.getElementById('review-container')
         );
      };

      return (
         isSaveInProgress ? reviewLoadingSpinner :
            isOwner ? <DatePicker className="review-by-date-picker"
               fixedHeight
               popperModifiers={[
                  {
                     name: 'offset',
                     options: {
                        offset: [-17, 0]
                     }
                  },
                  {
                     name: 'preventOverflow',
                     options: {
                        rootBoundary: 'viewport',
                        tether: false
                     }
                  }
               ]}
               popperContainer={calendarContainer}
               popperPlacement="bottom-start"
               customInput={<DateDisplay removeDate={this.removeDate}
                                       themeName={themeName}
                                       onClick={this.handleClickInput}
                                       innerRef={this.inputRef}/>}
               onClickOutside={this.onClickOutside}
               selected={defaultDate}
               value={review.ReviewByDate ? review.ReviewByDate.toDateString() : ''}
               onSelect={this.handleDatePickerSelection}
               onChange={this.handleDatePickerChange}
               minDate={new Date()}
               todayButton={Strings.labels.datePickerToday}
               calendarClassName="review-by-date-calendar review-page-calendar-popup"
            /> :
            <div className="review-by-date-text">{review.ReviewByDate ? `${Strings.labels.reviewDeadlinePrefix} ${displayDate}` : Strings.defaults.deadlinePlaceholder}</div>
      );
   }
}

export interface ReviewByDateProps extends Themeable {
   currentUserId: string;
   review: ReviewModel;
   setModalState(modalState: ModalStates, modalEventHandlersModel: ModalEventHandlersModel): void;
}

export interface ReviewByDateState {
   isSaveInProgress: boolean;
}
